import React, { useState, useEffect } from 'react';
import { interviewAPI, interviewSkeletonAPI, skeletonJobAssociationAPI, jobHiringManagerAPI } from '../../services/api';
import { jobService, JobDTO } from '../../services/jobService';
import { InterviewSkeleton, AssignInterviewRequest, Interview, InterviewStatus } from '../../types/interview';
import { getProfilePictureUrl, getUserInitials } from '../../utils/profilePictureUtils';
import { useAuth } from '../../contexts/AuthContext';
import { 
  UserIcon,
  BriefcaseIcon,
  XMarkIcon,
  UserGroupIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ChevronUpDownIcon,
  CheckIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Listbox, Transition } from '@headlessui/react';
import { Fragment } from 'react';

interface Application {
  id: number;
  candidateName: string;
  candidateEmail: string;
  jobTitle: string;
  jobId: number;
  status: string;
  appliedAt: string;
  candidateProfilePictureUrl?: string;
  candidateLinkedinProfileUrl?: string;
}

interface Interviewer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

// Add toast notification component
const Toast: React.FC<{ message: string; type: 'success' | 'error'; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm w-full ${
      type === 'success' 
        ? 'bg-green-100 border-green-500 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300' 
        : 'bg-red-100 border-red-500 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
    } border rounded-lg shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] p-4`}>
      <div className="flex items-center">
        <span className="font-medium">{message}</span>
        <button
          onClick={onClose}
          className="ml-auto -mr-1 -mt-1 h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const InterviewAssignmentPage: React.FC = () => {
  const { user } = useAuth();
  const isHiringManager = user?.role === 'HIRING_MANAGER';
  const [applications, setApplications] = useState<Application[]>([]);
  const [interviewers, setInterviewers] = useState<Interviewer[]>([]);
  const [skeletons, setSkeletons] = useState<InterviewSkeleton[]>([]);
  const [filteredSkeletons, setFilteredSkeletons] = useState<InterviewSkeleton[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [jobs, setJobs] = useState<JobDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Filter states
  const [jobFilter, setJobFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [interviewToCancel, setInterviewToCancel] = useState<Interview | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    interviewerId: '',
    skeletonId: '',
    scheduledAt: '',
    durationMinutes: '',
    locationType: 'OFFICE' as 'OFFICE' | 'ONLINE',
    locationAddress: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isHiringManager) {
        // HM flow: load only assigned jobs data
        const [myJobsResponse, interviewersResponse, skeletonsResponse] = await Promise.all([
          jobHiringManagerAPI.getMyJobs(),
          interviewAPI.getAvailableInterviewers(),
          interviewSkeletonAPI.getAll(),
        ]);

        const assignedJobs = myJobsResponse.data.assignedJobs || [];

        // For each assigned job, fetch shortlisted applications and interviews
        const [shortlistedResponses, interviewResponses] = await Promise.all([
          Promise.all(assignedJobs.map((job: any) => interviewAPI.getShortlistedApplications(job.id))),
          Promise.all(assignedJobs.map((job: any) => interviewAPI.getByJobId(job.id))),
        ]);

        const allShortlisted = shortlistedResponses.flatMap(r => r.data);
        const allInterviews = interviewResponses.flatMap(r => r.data);

        const mappedApplications: Application[] = allShortlisted.map((app: any) => ({
          id: app.id,
          candidateName: app.candidateName,
          candidateEmail: app.candidateEmail,
          jobTitle: app.jobTitle,
          jobId: app.jobId,
          status: 'SHORTLISTED',
          appliedAt: app.appliedAt,
          candidateProfilePictureUrl: app.candidateProfilePictureUrl,
          candidateLinkedinProfileUrl: app.candidateLinkedinProfileUrl,
        }));

        const mappedInterviewers: Interviewer[] = interviewersResponse.data.map((interviewer: any) => ({
          id: interviewer.id,
          firstName: interviewer.firstName,
          lastName: interviewer.lastName,
          email: interviewer.email,
        }));

        // Map assigned jobs to JobDTO-compatible shape for the filter dropdown
        const mappedJobs = assignedJobs.map((j: any) => ({ id: j.id, title: j.title } as JobDTO));

        setApplications(mappedApplications);
        setInterviewers(mappedInterviewers);
        setSkeletons(skeletonsResponse.data);
        setInterviews(allInterviews);
        setJobs(mappedJobs);
      } else {
        // Admin flow: load all data
        const [applicationsResponse, interviewersResponse, skeletonsResponse, interviewsResponse, jobsResponse] = await Promise.all([
          interviewAPI.getAllShortlistedApplications(),
          interviewAPI.getAvailableInterviewers(),
          interviewSkeletonAPI.getAll(),
          interviewAPI.getAllInterviews(),
          jobService.getAllJobs()
        ]);

        const mappedApplications: Application[] = applicationsResponse.data.map((app: any) => ({
          id: app.id,
          candidateName: app.candidateName,
          candidateEmail: app.candidateEmail,
          jobTitle: app.jobTitle,
          jobId: app.jobId,
          status: 'SHORTLISTED',
          appliedAt: app.appliedAt,
          candidateProfilePictureUrl: app.candidateProfilePictureUrl,
          candidateLinkedinProfileUrl: app.candidateLinkedinProfileUrl
        }));

        const mappedInterviewers: Interviewer[] = interviewersResponse.data.map((interviewer: any) => ({
          id: interviewer.id,
          firstName: interviewer.firstName,
          lastName: interviewer.lastName,
          email: interviewer.email
        }));

        setApplications(mappedApplications);
        setInterviewers(mappedInterviewers);
        setSkeletons(skeletonsResponse.data);
        setInterviews(interviewsResponse.data);
        setJobs(jobsResponse);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get all interviews for a specific application
  const getInterviewsForApplication = (applicationId: number): Interview[] => {
    return interviews.filter(interview => interview.applicationId === applicationId);
  };

  // Filter applications based on job and search term
  const getFilteredApplications = () => {
    return applications.filter(app => {
      const matchesJob = !jobFilter || app.jobId.toString() === jobFilter;
      const matchesSearch = !searchTerm || 
        app.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.candidateEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesJob && matchesSearch;
    });
  };

  const handleAssignInterview = async (application: Application) => {
    setSelectedApplication(application);
    setError(null); // Clear previous errors
    setFormData({
      interviewerId: '',
      skeletonId: '',
      scheduledAt: '',
      durationMinutes: '',
      locationType: 'OFFICE',
      locationAddress: '',
      notes: ''
    });
    
    try {
      // Get skeletons associated with this job
      const associatedSkeletonIds = await skeletonJobAssociationAPI.getSkeletonIdsByJobId(application.jobId);
      
      // Filter skeletons to only show those associated with the job
      const jobSkeletons = skeletons.filter(skeleton => 
        associatedSkeletonIds.data.includes(skeleton.id)
      );
      
      // STRICT: Only show skeletons associated with this job
      setFilteredSkeletons(jobSkeletons);
    } catch (error) {
      console.error('Error loading job skeletons:', error);
      // STRICT: Show no skeletons if API fails
      setFilteredSkeletons([]);
    }
    
    setShowModal(true);
  };

  const handleCancelClick = (interview: Interview) => {
    setInterviewToCancel(interview);
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setInterviewToCancel(null);
  };

  // Handle interview cancellation
  const confirmCancelInterview = async () => {
    if (!interviewToCancel) return;

    setIsCancelling(true);
    try {
      await interviewAPI.cancelInterview(interviewToCancel.id);
      setToast({
        message: 'Interview assignment cancelled successfully',
        type: 'success'
      });
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Error cancelling interview:', error);
      setToast({
        message: 'Failed to cancel interview assignment',
        type: 'error'
      });
    } finally {
      setIsCancelling(false);
      closeCancelModal();
    }
  };

  // Handle viewing interview results
  const handleViewResults = (interview: Interview) => {
    setSelectedInterview(interview);
    setShowResultsModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedApplication || !formData.interviewerId || !formData.skeletonId || !formData.durationMinutes || !formData.locationType) {
      setError('Please fill in all required fields including duration and location type');
      return;
    }

    // Validate location address for office interviews
    if (formData.locationType === 'OFFICE' && (!formData.locationAddress || formData.locationAddress.trim() === '')) {
      setError('Please provide the office address for office interviews');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const requestData: AssignInterviewRequest = {
        applicationId: selectedApplication.id,
        interviewerId: parseInt(formData.interviewerId),
        skeletonId: parseInt(formData.skeletonId),
        scheduledAt: formData.scheduledAt || undefined,
        durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes) : undefined,
        locationType: formData.locationType,
        locationAddress: formData.locationAddress || undefined,
        notes: formData.notes || undefined,
        sendCalendarInvite: true // Always send calendar invites
      };

      await interviewAPI.assign(requestData);
      
      // Show success toast
      setToast({
        message: `Interview successfully assigned to ${interviewers.find(i => i.id === parseInt(formData.interviewerId))?.firstName} ${interviewers.find(i => i.id === parseInt(formData.interviewerId))?.lastName}`,
        type: 'success'
      });
      
      // Close modal and refresh data
      setShowModal(false);
      fetchData(); // Refresh the applications list
      
    } catch (err: any) { // Changed to any to access err.response
      console.error('Error assigning interview:', err);
      // Try to get the specific error message from the backend response
      const errorMessage = err.response?.data?.message || err.message || 'Failed to assign interview';
      setError(errorMessage);
      setToast({
        message: errorMessage,
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] rounded-lg border border-gray-200/50 dark:border-gray-700/50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Interview Assignment</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Assign interviews to shortlisted candidates
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 rounded-md p-4">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] rounded-lg border border-gray-200/50 dark:border-gray-700/50">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidates, emails, or job titles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 shadow-sm"
                />
              </div>
            </div>

            {/* Job Filter */}
            <div className="w-full sm:w-64">
              <Listbox value={jobFilter} onChange={setJobFilter}>
              <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-700 py-3 pl-3 pr-10 text-left shadow-sm focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500 sm:text-sm border border-gray-300 dark:border-gray-600">
                    <span className="block truncate">{jobs.find(j => j.id.toString() === jobFilter)?.title || 'All Jobs'}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-3 text-base shadow-xl ring-1 ring-black/5 focus:outline-none sm:text-sm border border-gray-300 dark:border-gray-600">
                      <Listbox.Option
                        key="all-jobs"
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-indigo-50 dark:bg-gray-700' : 'text-gray-900 dark:text-gray-100'
                          }`
                        }
                        value=""
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? 'font-medium text-indigo-600 dark:text-indigo-400' : 'font-normal'
                              }`}
                            >
                              All Jobs
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                      {jobs.map((job) => (
                        <Listbox.Option
                          key={job.id}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-indigo-50 dark:bg-gray-700' : 'text-gray-900 dark:text-gray-100'
                            }`
                          }
                          value={job.id.toString()}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? 'font-medium text-indigo-600 dark:text-indigo-400' : 'font-normal'
                                }`}
                              >
                      {job.title}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
              </div>
              </Listbox>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.3),0_4px_6px_-2px_rgba(0,0,0,0.2)] rounded-lg border border-gray-200/50 dark:border-gray-700/50">
        <div className="px-4 py-4 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Shortlisted Applications ({getFilteredApplications().length})
          </h2>
        </div>

        {getFilteredApplications().length === 0 ? (
          <div className="px-6 py-12 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {applications.length === 0 ? 'No shortlisted applications' : 'No applications match your filters'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {applications.length === 0 
                ? 'Applications will appear here when they are shortlisted for interviews.'
                : 'Try adjusting your search or job filter to see more results.'
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {getFilteredApplications().map((application) => {
              const applicationInterviews = getInterviewsForApplication(application.id);
              
              return (
                <div key={application.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
                        <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        {(() => {
                          const profilePictureUrl = getProfilePictureUrl({
                            profilePictureUrl: application.candidateProfilePictureUrl,
                            linkedinProfileUrl: application.candidateLinkedinProfileUrl,
                            name: application.candidateName
                          });
                          const initials = getUserInitials({ name: application.candidateName });
                          
                          return profilePictureUrl ? (
                            <img
                              src={profilePictureUrl}
                              alt={application.candidateName}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-offset-2 dark:ring-offset-gray-800 ring-indigo-500 dark:ring-indigo-400 flex-shrink-0"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-800 dark:text-indigo-300 text-sm font-medium ring-2 ring-offset-2 dark:ring-offset-gray-800 ring-indigo-500 dark:ring-indigo-400 flex-shrink-0">
                              {initials}
                            </div>
                          );
                        })()}
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {application.candidateName}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{application.candidateEmail}</p>
                        </div>
                      </div>
                      
                            <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                                    <BriefcaseIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                                    <span className="truncate">{application.jobTitle}</span>
                        </div>
                                <span className="hidden sm:inline">•</span>
                                <span className="mt-1 sm:mt-0">Applied: {formatDate(application.appliedAt)}</span>
                                <span className="hidden sm:inline">•</span>
                                <div className="mt-2 sm:mt-0 flex items-center gap-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          {application.status}
                        </span>
                        {applicationInterviews.length > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                              {applicationInterviews.length} Interview{applicationInterviews.length !== 1 ? 's' : ''}
                            </span>
                        )}
                                </div>
                      </div>

                      {/* Display all interviews for this application */}
                            <div className="flex-grow mt-4">
                                {applicationInterviews.length > 0 ? (
                                    <div className="space-y-4">
                                        {applicationInterviews.map((interview) => (
                                            <div key={interview.id} className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200/50 dark:border-blue-800/50 flex-grow w-full">
                                                    <div className="flex items-center mb-2">
                                                        <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />
                                                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">{interview.skeletonName}</h4>
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1 pl-7">
                                                        <p>
                                                            <span className="font-medium">Assigned to:</span> {interview.interviewerName}
                                                        </p>
                                                        {interview.scheduledAt && (
                                                            <p>
                                                                <span className="font-medium">Scheduled:</span> {formatDate(interview.scheduledAt)}
                                                            </p>
                                                        )}
                                                        <p>
                                                            <span className="font-medium">Status:</span> 
                                                            <span className={`ml-1 font-semibold ${
                                                                interview.status === InterviewStatus.COMPLETED ? 'text-green-600 dark:text-green-400' :
                                                                interview.status === InterviewStatus.IN_PROGRESS ? 'text-yellow-600 dark:text-yellow-400' :
                                                                interview.status === InterviewStatus.ASSIGNED ? 'text-blue-600 dark:text-blue-400' :
                                                                'text-gray-600 dark:text-gray-400'
                                                            }`}>
                                                                {interview.status}
                                </span>
                                                        </p>
                                                        {interview.completedAt && (
                                <p>
                                                                <span className="font-medium">Completed:</span> {formatDate(interview.completedAt)}
                                </p>
                                    )}
                              </div>
                            </div>
                                                <div className="w-full sm:w-auto flex flex-col items-center space-y-2 flex-shrink-0">
                              <button
                                                        type="button"
                                onClick={() => handleAssignInterview(application)}
                                                        className="w-full sm:w-36 inline-flex items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-200 whitespace-nowrap"
                                                        title="Assign another interview for this candidate"
                                                    >
                                                        <CalendarDaysIcon className="-ml-1 mr-2 h-5 w-5 flex-shrink-0" />
                                                        <span>Add Interview</span>
                                    </button>
                                    {interview.status === 'COMPLETED' && (
                                        <button
                                            onClick={() => {
                                                setSelectedInterview(interview);
                                                setShowResultsModal(true);
                                            }}
                                            className="w-full sm:w-36 inline-flex items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 dark:from-green-500 dark:to-green-600 dark:hover:from-green-600 dark:hover:to-green-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-200 whitespace-nowrap"
                                        >
                                            <EyeIcon className="-ml-1 mr-2 h-5 w-5 flex-shrink-0" />
                                            <span>View Results</span>
                              </button>
                                    )}
                                    {interview.status !== 'COMPLETED' && (
                              <button
                                onClick={() => handleCancelClick(interview)}
                                            className="w-full sm:w-36 inline-flex items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 dark:from-red-500 dark:to-red-600 dark:hover:from-red-600 dark:hover:to-red-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-200 whitespace-nowrap"
                                            title="Cancel this interview assignment"
                              >
                                            <TrashIcon className="-ml-1 mr-2 h-5 w-5 flex-shrink-0" />
                                            <span className="truncate">Cancel Interview</span>
                              </button>
                                    )}
                              </div>
                            </div>
                          ))}
                        </div>
                                ) : (
                                    <div className="flex items-center justify-end mt-4">
                        <button
                                            type="button"
                          onClick={() => handleAssignInterview(application)}
                                            className="w-full sm:w-36 inline-flex items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-200 whitespace-nowrap"
                        >
                                            <CalendarDaysIcon className="-ml-1 mr-2 h-5 w-5 flex-shrink-0" />
                                            <span>Add Interview</span>
                        </button>
                      </div>
                    )}
                            </div>
                        </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && selectedApplication && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5),0_10px_10px_-5px_rgba(0,0,0,0.3)] transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-gray-200/50 dark:border-gray-700/50">
              <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
              <button
                  type="button"
                onClick={() => setShowModal(false)}
                  className="rounded-lg bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
              >
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Assign Interview - {selectedApplication.candidateName}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  {error && (
                    <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 rounded-md p-3">
                      <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
                    </div>
                  )}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Candidate Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Name:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedApplication.candidateName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Email:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedApplication.candidateEmail}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Position:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedApplication.jobTitle}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Applied:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatDate(selectedApplication.appliedAt)}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Interviewer *</label>
                    <Listbox value={formData.interviewerId} onChange={(value) => setFormData(prev => ({ ...prev, interviewerId: value }))}>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-700 py-2 pl-3 pr-10 text-left shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 sm:text-sm transition-all duration-200 border border-gray-300 dark:border-gray-600">
                          <span className="block truncate">{interviewers.find(i => i.id.toString() === formData.interviewerId)?.firstName || 'Select an interviewer'} {interviewers.find(i => i.id.toString() === formData.interviewerId)?.lastName}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {interviewers.map((interviewer) => (
                              <Listbox.Option
                                key={interviewer.id}
                                className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-100'}`}
                                value={interviewer.id.toString()}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{interviewer.firstName} {interviewer.lastName} ({interviewer.email})</span>
                                    {selected ? <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span> : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Interview Template *</label>
                    <Listbox value={formData.skeletonId} onChange={(value) => setFormData(prev => ({ ...prev, skeletonId: value }))}>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-700 py-2 pl-3 pr-10 text-left shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 sm:text-sm transition-all duration-200 border border-gray-300 dark:border-gray-600">
                          <span className="block truncate">{skeletons.find(s => s.id.toString() === formData.skeletonId)?.name || 'Select an interview template'}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {filteredSkeletons.map((skeleton) => (
                              <Listbox.Option
                                key={skeleton.id}
                                className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'text-gray-900 dark:text-gray-100'}`}
                                value={skeleton.id.toString()}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{skeleton.name} ({skeleton.focusAreas.length} focus areas)</span>
                                    {selected ? <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600 dark:text-indigo-400"><CheckIcon className="h-5 w-5" aria-hidden="true" /></span> : null}
                                  </>
                                )}
                              </Listbox.Option>
                  ))}
                  {filteredSkeletons.length === 0 && (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-400">No interview templates associated with this job</div>
                  )}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Scheduled Date & Time</label>
                    <div className="relative mt-1">
                <input
                  type="datetime-local"
                  value={formData.scheduledAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
                        className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 shadow-sm transition-all duration-200"
                />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <CalendarDaysIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                    </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Duration (minutes) <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.durationMinutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 shadow-sm transition-all duration-200"
                  required
                >
                  <option value="">Select duration</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="75">75 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="105">105 minutes</option>
                  <option value="120">120 minutes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Interview Location <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="locationType"
                      value="OFFICE"
                      checked={formData.locationType === 'OFFICE'}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        locationType: e.target.value as 'OFFICE' | 'ONLINE'
                      }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Office Interview</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="locationType"
                      value="ONLINE"
                      checked={formData.locationType === 'ONLINE'}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        locationType: e.target.value as 'OFFICE' | 'ONLINE'
                      }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Online Interview</span>
                  </label>
                </div>
              </div>

              {formData.locationType === 'OFFICE' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Office Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.locationAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, locationAddress: e.target.value }))}
                    placeholder="Enter the office address for the interview"
                    className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 shadow-sm transition-all duration-200"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 shadow-sm transition-all duration-200"
                  placeholder="Optional notes for the interviewer"
                />
              </div>

              {/* Calendar Integration Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      📅 Calendar Invites
                    </h3>
                    <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                      {formData.locationType === 'ONLINE' ? (
                        <p>Calendar invites will be sent automatically with meeting link to all participants (interviewer, candidate, and admin).</p>
                      ) : (
                        <p>Calendar invites will be sent automatically with office address to all participants (interviewer, candidate, and admin).</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 dark:from-indigo-500 dark:to-indigo-600 dark:hover:from-indigo-600 dark:hover:to-indigo-700 disabled:opacity-50 font-medium transform hover:scale-[1.02]"
                >
                  {submitting ? 'Assigning...' : 'Assign Interview'}
                </button>
              </div>
            </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interview Results Modal */}
      {showResultsModal && selectedInterview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5),0_10px_10px_-5px_rgba(0,0,0,0.3)] transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full border border-gray-200/50 dark:border-gray-700/50">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Interview Results</h2>
                <button
                  onClick={() => setShowResultsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
              <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
              {/* Interview Summary */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Candidate</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedInterview.application.candidateName}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Job Position</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedInterview.application.jobTitle}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Interviewer</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedInterview.interviewerName}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Interview Template</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedInterview.skeletonName}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed Date</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {selectedInterview.completedAt 
                        ? new Date(selectedInterview.completedAt).toLocaleDateString()
                        : 'N/A'
                      }
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Overall Rating</h3>
                    <p className={`text-2xl font-bold ${
                      selectedInterview.responses && selectedInterview.responses.length > 0
                        ? (() => {
                            const avgRating = selectedInterview.responses
                              .filter(r => r.rating > 0)
                              .reduce((sum, r) => sum + r.rating, 0) / 
                              selectedInterview.responses.filter(r => r.rating > 0).length;
                            return avgRating >= 70
                              ? 'text-green-600 dark:text-green-400'
                              : avgRating >= 50
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400';
                          })()
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {selectedInterview.responses && selectedInterview.responses.length > 0
                        ? (() => {
                            const validRatings = selectedInterview.responses.filter(r => r.rating > 0);
                            if (validRatings.length === 0) return 'N/A';
                            const avgRating = validRatings.reduce((sum, r) => sum + r.rating, 0) / validRatings.length;
                            return `${Math.round(avgRating)}/100`;
                          })()
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Focus Area Results */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Focus Area Results</h3>
                <div className="space-y-4">
                  {selectedInterview.responses && selectedInterview.responses.length > 0 ? (
                    selectedInterview.responses.map((response, index) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">{response.title}</h4>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-sm font-medium ${
                              response.rating >= 70
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : response.rating >= 50
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : response.rating > 0
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                            }`}>
                              {response.rating > 0 ? `${response.rating}/100` : 'Not Rated'}
                            </span>
                          </div>
                        </div>
                        {response.feedback && (
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3">
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {response.feedback}
                            </p>
                          </div>
                        )}
                        {!response.feedback && (
                          <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                              No feedback provided for this focus area.
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No detailed responses available.</p>
                  )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showCancelModal && interviewToCancel && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-6 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75 transition-opacity" 
              onClick={closeCancelModal}
              aria-hidden="true"
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.5),0_10px_10px_-5px_rgba(0,0,0,0.3)] transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full border border-gray-200 dark:border-gray-700">
              <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                <button
                  type="button"
                  onClick={closeCancelModal}
                  className="rounded-lg bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                >
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="px-6 py-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30">
                    <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg leading-6 font-semibold text-gray-900 dark:text-gray-100">
                      Cancel Interview
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        Are you sure you want to cancel the interview for{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {interviewToCancel.application.candidateName}
                        </span>
                        ?
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                        This action will notify the candidate and interviewer, and the interview will be removed from their calendars. This cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
                  <button
                    type="button"
                    onClick={closeCancelModal}
                    disabled={isCancelling}
                    className={`w-full sm:w-auto inline-flex justify-center items-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2.5 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all duration-200 ${isCancelling ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Keep Interview
                  </button>
                  <button
                    type="button"
                    onClick={confirmCancelInterview}
                    disabled={isCancelling}
                    className={`w-full sm:w-auto inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 dark:from-red-500 dark:to-red-600 dark:hover:from-red-600 dark:hover:to-red-700 text-sm font-medium text-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-red-400 transform hover:scale-[1.02] transition-all duration-200 ${isCancelling ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isCancelling ? (
                      <>
                        <div className="animate-spin -ml-1 mr-2 h-5 w-5 border-2 border-white rounded-full border-t-transparent" />
                        Cancelling...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                        Yes, Cancel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default InterviewAssignmentPage; 